import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import { assertNotEmpty, notEmpty, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import {
  FormikCheckbox,
  FormikForm,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { TextInput } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import * as Yup from 'yup'

import { server as serverPath } from '../../../../system/paths'
import { useNavigationParams } from '../../../../system/useNavigationParams'
import { isEqualBranch } from '../../../../uiModels/branches/helpers'
import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { useAutomaticWorkspaceName } from '../../../../uiModels/workspace/useAutomaticWorkspaceName'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { useOpenActiveElement } from '../../useOpenActiveElement'
import type { BranchDetailsNavigationState } from '../branchDetailsNavigationState'
import { downloadConfigurationFile } from '../helpers'
import { CurrentMergeStatus } from './CurrentMergeStatus'
import { MergeConflicts } from './MergeConflicts'
import { isFinalMergeCompareStatus, isFinalMergeStatus } from './types'
import { useMergeBranch } from './useMergeBranch'
import { useMergeComparison } from './useMergeComparison'

export type MergeBranchInput = {
  mergeBranchName: string
  shouldSquash: boolean
  openBranchAfterMerge: boolean
}

const validate = typeUtils.createValidator<MergeBranchInput>()

type Props = {
  targetBranchKey: BranchKey
  sourceBranchKey: BranchKey
  onDidMerge: (revalidateWorkspaces: boolean) => void
}

export const MergeBranchForm = ({
  targetBranchKey,
  sourceBranchKey,
  onDidMerge,
}: Props) => {
  const translate = useTranslate()
  const allBranches = useAllConciseBranches()
  const {
    compare,
    status: compareStatus,
    branchChangesets,
    conflictChangesets,
    error: compareError,
  } = useMergeComparison(targetBranchKey)
  const {
    mutateAsync: mergeBranches,
    status: mergeStatus,
    error: mergeError,
  } = useMergeBranch()

  const targetBranch = allBranches[targetBranchKey.server]?.find(
    (branch) => branch.name === targetBranchKey.name,
  )
  const isTargetProtected = isProtectedBranchCategory(
    targetBranch?.branchCategory,
  )

  const { onSwitch } = useWorkspaceManagerContext()
  const openActiveElement = useOpenActiveElement()
  const navigate = useNavigate()
  const {
    server: currentServer,
    branch: currentBranch,
    block,
    layer,
  } = useNavigationParams()
  const mergeConflictsWorkspaceName = useAutomaticWorkspaceName({
    workspaceNamePrefix: `merge-conflicts-from-${sourceBranchKey.name}`,
    server: targetBranchKey.server,
  })

  const isTargetOpen = isEqualBranch(
    targetBranchKey,
    currentBranch ? { name: currentBranch, server: currentServer } : undefined,
  )

  useEffect(() => {
    void compare(sourceBranchKey.name)
  }, [compare, sourceBranchKey.name])

  const validationSchema = Yup.object({
    mergeBranchName: isTargetProtected
      ? Yup.string()
          .required(translate('validation.required'))
          .noSpaces(translate('validation.noSpaces'))
          .notForbidden(translate('validation.notForbidden'), [STANDARD_BRANCH])
          .notDuplicated(
            translate('validation.duplicated'),
            (allBranches?.[targetBranchKey.server] ?? [])
              .map((conciseBranch) => conciseBranch.name)
              .filter(notEmpty),
          )
      : Yup.string().optional(),
  })

  const initialValues: MergeBranchInput = {
    mergeBranchName: `merge-${sourceBranchKey.name}-to-${targetBranchKey.name}`,
    shouldSquash: false,
    openBranchAfterMerge: false,
  }

  const handleMergeBranch = async (values: MergeBranchInput) => {
    assertNotEmpty(branchChangesets)

    const server = targetBranchKey.server
    const sourceBranch = allBranches[server]?.find(
      (branch) => branch.name === sourceBranchKey.name,
    )

    assertNotEmpty(targetBranch)
    assertNotEmpty(sourceBranch)

    const result = await mergeBranches({
      ...values,
      mergeConflictsWorkspaceName,
      branchChangesets,
      workspaceChangesets: conflictChangesets,
      targetBranch,
      sourceBranch,
      server: targetBranchKey.server,
    })

    if (!result) {
      return
    }

    onDidMerge(!!conflictChangesets)

    if (result.conflictsWorkspaceKey) {
      if (isTargetOpen) {
        // The target branch is already open, so instead of navigating we open
        // the new conflicts workspace and reveal its Workspace Changes panel.
        await onSwitch(result.conflictsWorkspaceKey, {
          state: {
            initialTab: 'operations',
          } satisfies BranchDetailsNavigationState,
        })
        openActiveElement(result.conflictsWorkspaceKey)
      } else if (values.openBranchAfterMerge) {
        await onSwitch(result.conflictsWorkspaceKey, {
          state: {
            initialTab: 'operations',
          } satisfies BranchDetailsNavigationState,
        })
      }
    } else if (!isTargetOpen && values.openBranchAfterMerge) {
      await navigate(
        serverPath({
          server: result.hostBranchKey.server,
          branch: result.hostBranchKey.name,
          block,
          layer,
        }),
      )
    }
  }

  const isMergeInProgress = mergeStatus && !isFinalMergeStatus(mergeStatus)

  if (!compareStatus) {
    return null
  }

  if (!isFinalMergeCompareStatus(compareStatus)) {
    return (
      <Alert isLoading>
        {translate(
          'branchDetailsPanel.tabs.manage.merge.enum.compareStatus.comparing',
        )}
      </Alert>
    )
  }

  return compareStatus === 'success' && !compareError ? (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleMergeBranch}
    >
      {({ values }) => (
        <Stack gap="display" className="flex-col">
          {isTargetProtected && (
            <Alert
              severity="info"
              title={translate(
                'branchDetailsPanel.tabs.manage.merge.form.protectedBranch.warning',
                { targetBranchName: targetBranchKey.name },
              )}
            />
          )}
          {branchChangesets && (
            <div>
              <Button
                variant="discreet"
                className="p-0! mr-1 underline"
                onClick={() =>
                  downloadConfigurationFile(
                    branchChangesets,
                    `merge-changesets.json`,
                  )
                }
              >
                {translate(
                  'branchDetailsPanel.tabs.manage.merge.changesets.downloadButton',
                )}
              </Button>
              <span>
                {translate(
                  'branchDetailsPanel.tabs.manage.merge.branchChangesets.text',
                )}
              </span>
            </div>
          )}
          {!!conflictChangesets && (
            <MergeConflicts
              conflictChangesets={conflictChangesets}
              mergeConflictsWorkspaceName={mergeConflictsWorkspaceName}
              sourceBranchName={sourceBranchKey.name}
              targetBranchName={targetBranchKey.name}
            />
          )}
          {isTargetProtected && (
            <FormikTextInput
              name={validate('mergeBranchName')}
              label={translate(
                'branchDetailsPanel.tabs.manage.merge.form.mergeBranchName.label',
              )}
              description={translate(
                'branchDetailsPanel.tabs.manage.merge.form.mergeBranchName.description',
              )}
            />
          )}
          {!!conflictChangesets && (
            <TextInput
              disabled
              value={mergeConflictsWorkspaceName}
              wrapperProps={{
                label: translate(
                  'branchDetailsPanel.tabs.manage.merge.form.mergeConflictsWorkspaceName.label',
                ),
                description: translate(
                  'branchDetailsPanel.tabs.manage.merge.form.mergeConflictsWorkspaceName.description',
                ),
              }}
            />
          )}
          {!isTargetProtected && (
            <FormikCheckbox
              name={validate('shouldSquash')}
              label={translate(
                'branchDetailsPanel.tabs.manage.merge.form.squash.label',
              )}
              description={translate(
                'branchDetailsPanel.tabs.manage.merge.form.squash.description',
                { targetBranchName: targetBranchKey.name },
              )}
            />
          )}
          {!isTargetOpen && (
            <FormikCheckbox
              name={validate('openBranchAfterMerge')}
              label={translate(
                'branchDetailsPanel.tabs.manage.merge.form.openBranchAfterMerge.label',
              )}
              description={translate(
                'branchDetailsPanel.tabs.manage.merge.form.openBranchAfterMerge.description',
              )}
            />
          )}
          {mergeStatus && (
            <CurrentMergeStatus
              mergeStatus={mergeStatus}
              mergeError={mergeError}
              targetBranchKey={targetBranchKey}
              isTargetProtected={isTargetProtected}
              conflictsWorkspaceName={mergeConflictsWorkspaceName}
              formValues={values}
            />
          )}
          <div className="flex w-full justify-end">
            <Button
              type="submit"
              isLoading={isMergeInProgress}
              disabled={isMergeInProgress || mergeStatus === 'error'}
            >
              {isTargetProtected
                ? translate(
                    'branchDetailsPanel.tabs.manage.merge.form.submitButton',
                  )
                : translate(
                    'branchDetailsPanel.tabs.manage.merge.form.mergeButton',
                  )}
            </Button>
          </div>
        </Stack>
      )}
    </FormikForm>
  ) : (
    <Alert severity="error" title={compareError} />
  )
}
